import {EMPTY_TEMPLATE, RegistryState, ServiceProject, ServiceProjectEnabledStatus, ServiceProjectStatus} from "../../domain/Service";
import {httpDelete, httpGetJSON, httpPostEmpty, httpPostJSON, httpPutFile, httpPutJSON, signUpToken} from "./Fetch";
import {appConfig, customerPrefix} from "../cfg/config";
import {conditionToQueryString} from "./QueryString";
import {TEMPLATES_MAP} from "./TemplatesSet";
import {Converter} from "./Converter";
import {ChangeOptions, GetCondition, ListItemApi} from "./ListItemApi";

interface ProjectApiObject {
  id: string;
  name: string;
  owner: string;
  registry?: RegistryState | null;
  did?: string;
  description: string;
  shortDescription: string;
  logo: string;
  banner: string;
  categories: string[];
  countries: string[];
  keywords: string[];
  status: ServiceProjectStatus;
  paymentPlan?: string;
  addons?: string[];
  onPrem?: boolean;
  templateName?: string;
  enabledStatus: ServiceProjectEnabledStatus;
  isPublic: boolean;
}

class ProjectConverter extends Converter<ServiceProject, ProjectApiObject> {
  apiObjectToItem(obj: ProjectApiObject, parent?: undefined): ServiceProject {
    const item: ServiceProject = {...obj, identity: obj.id, paymentPlan: obj.paymentPlan};
    if (obj.logo) {
      item.logo = `${appConfig().ZAKA_BACKEND}${obj.logo}`;
    }
    if (obj.banner) {
      item.banner = `${appConfig().ZAKA_BACKEND}${obj.banner}`;
    }
    if (obj.templateName) {
      item.template = TEMPLATES_MAP.get(obj.templateName) || EMPTY_TEMPLATE;
    } else {
      item.template = EMPTY_TEMPLATE;
    }
    return item;
  }

  itemToApiObject(item: ServiceProject): ProjectApiObject {
    const res: ProjectApiObject = {...item, id: item.identity, paymentPlan: item.paymentPlan};
    if (item.template && item.template !== EMPTY_TEMPLATE) {
      res.templateName = item.template.name;
    }
    delete (res as any).template;
    return res;
  }
}

const converterObject = {
  identity: 'id',
};

export class ProjectApi extends ListItemApi<ServiceProject, ProjectApiObject> {
  constructor() {
    super(new ProjectConverter());
  }

  async checkName(name: string) {
    // return {ok: true};
    return httpGetJSON(`${customerPrefix()}/project/check/${encodeURI(name)}`, signUpToken());
  }

  async sendToReview(project: ServiceProject) {
    const prj = await httpPostEmpty(`${customerPrefix()}/to-review/${encodeURI(project.identity)}`, signUpToken());
    return this.converter.apiObjectToItem(prj);
  }

  async updateLogo(service: ServiceProject, file: File) {
    if (!file) {
      return;
    }
    await httpPutFile(`${customerPrefix()}/project/${encodeURI(service.identity)}/logo`, file, 'logo', signUpToken());
  }

  async updateBanner(service:ServiceProject, file: File) {
    if (!file) {
      return;
    }
    await httpPutFile(`${customerPrefix()}/project/${encodeURI(service.identity)}/banner`, file, 'banner', signUpToken());
  }

  async disable(project: ServiceProject) {
    const did = project.did;
    if (!did) {
      throw new Error('Unable to disable not approved service');
    }
    await httpPostEmpty(`${customerPrefix()}/disable/${encodeURI(did)}`, signUpToken());
  }

  async enable(project: ServiceProject) {
    const did = project.did;
    if (!did) {
      throw new Error('Unable to enable not approved service');
    }
    await httpPostEmpty(`${customerPrefix()}/enable/${encodeURI(did)}`, signUpToken());
  }

  protected async fetchOne(condition?: GetCondition): Promise<ProjectApiObject> {
    const id = condition && condition.identity ? condition.identity : '';
    return await httpGetJSON(`${customerPrefix()}/project/${encodeURI(id as string)}`, signUpToken());
  }

  protected async fetchList(condition?: GetCondition) {
    // return {itemsTotal: FAKE_PROJECTS.length, items: FAKE_PROJECTS};
    const resp = await httpGetJSON(
      `${customerPrefix()}/projects${conditionToQueryString(condition, converterObject)}`,
      signUpToken()
    );
    return resp;
  }

  protected async fetchCreate(item: ProjectApiObject): Promise<ProjectApiObject> {
    const d: any = {...item};
    delete d.id;
    const created = await httpPostJSON(`${customerPrefix()}/project`, d, signUpToken());
    return created;
  }

  protected async fetchSave(item: ProjectApiObject, opt?: ChangeOptions): Promise<void> {
    console.log('i call save');
    const prevName = opt ? opt.prevIdentity : item.id;
    await httpPutJSON(`${customerPrefix()}/project/${encodeURI(prevName as string)}`, item, signUpToken());
  }

  protected async fetchDelete(item: ProjectApiObject): Promise<void> {
    await httpDelete(`${customerPrefix()}/project/${encodeURI(item.id)}`, signUpToken());
  }

}

// t4-hardening T4 — proof of control for the service's own Stellar account.
// Both calls are proxied by the platform: the registrar bridge is
// loopback-only on the box and is never reachable from a browser.
export async function stellarChallenge(draftId: string, stellarAddress: string) {
  return httpPostJSON(`${customerPrefix()}/stellar-challenge`, {draftId, stellarAddress}, signUpToken());
}

/// Which network the registry is on, so the form can name it. Never guessed
/// client-side: an applicant who creates an account on the wrong network only
/// finds out at registration, long after the key exists.
export async function stellarNetwork(): Promise<{network: string | null; contractId: string | null}> {
  return httpGetJSON(`${customerPrefix()}/stellar-network`, signUpToken());
}

export async function stellarProof(draftId: string, nonce: string, signedXdr: string) {
  // The signing-key consent that rode here (signing-key-follows-account T2)
  // went with anchor-rail-leftovers D7: the proof proves control, nothing else.
  return httpPostJSON(`${customerPrefix()}/stellar-proof`, {draftId, nonce, signedXdr}, signUpToken());
}

// account-conflict-check T3 — does the registry already hold this account as
// another service's identity? Asked the moment an address parses, so the
// contract's AccountAlreadyBound becomes an early sentence instead of a
// failed registration after review. `checked:false` = the platform could not
// look; the form must never read that as «free».
export interface HolderView {
  checked: boolean;
  held: {did: string; name: string | null; status: string; own: boolean} | null;
}

export async function stellarAccountHolder(address: string, projectId?: string): Promise<HolderView> {
  const q = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  return httpGetJSON(`${customerPrefix()}/stellar-account/${encodeURIComponent(address)}${q}`, signUpToken());
}

// ledger-adapter-boundary T4 — the adapter's self-description and the two
// ledger reads, through the platform. The browser used to call the chain's
// own services itself and to carry the address pattern, the network's label,
// the passphrase and the wallet's name; all of it is answered here now (D3).
export interface AdapterDescriptor {
  ok: boolean;
  ledger: string;
  network: string | null;
  networkLabel?: string | null;
  testNetwork?: boolean;
  contractId?: string | null;
  account?: {pattern: string; hint?: string; contractPattern?: string | null; unit?: string};
  funding?: {kind: string; via?: string | null; url?: string | null};
  explorer?: {tx?: string | null; account?: string | null};
  wallet?: {families?: string[]; passphrase?: string | null};
  howToGetAccount?: {walletUrl?: string; walletName?: string; labUrl?: string; cli?: string};
}

/// `{network, contractId, ledger, adapter}` — `adapter` is null when the
/// platform could not ask, which the form renders as «ask your
/// administrator», never as a guess.
export async function registryDescribe(): Promise<{network: string | null; contractId: string | null; ledger: string | null; adapter: AdapterDescriptor | null}> {
  return httpGetJSON(`${customerPrefix()}/registry/describe`, signUpToken());
}

export interface LedgerAccountState {exists: boolean; balance: string | null; unit?: string | null}

/// `{checked:false}` = could not find out, which is NOT «missing».
export async function registryAccountState(address: string): Promise<{checked: boolean; state: LedgerAccountState | null}> {
  return httpGetJSON(`${customerPrefix()}/registry/account-state/${encodeURIComponent(address)}`, signUpToken());
}

/// Only where the descriptor says a faucet exists; the platform relays the
/// adapter's refusal otherwise.
export async function registryFund(address: string) {
  return httpPostJSON(`${customerPrefix()}/registry/fund`, {address}, signUpToken());
}

// ── The service's own did:webvh log (webvh-log T4) ─────────────────────────
// Both calls are proxied by the platform: the registrar bridge is
// loopback-only on the box and is never reachable from a browser. Nothing
// here signs — the entries arrive already signed by the key in this browser,
// and the bridge re-verifies the whole log before it stores anything.

export interface LogPlan {
  ok: boolean;
  slug: string;
  host: string;
  /// `did:webvh:{SCID}:<host>:dids:<slug>` — the SCID is a placeholder
  /// because only the genesis being built can produce it.
  didTemplate: string;
  logUrl: string;
  exists: boolean;
  did: string | null;
  head: string | null;
  /// The log as stored, in full, so the browser can check it for itself.
  entries: any[];
  updateKeys: string[];
  nextKeyHashes: string[];
  verified?: boolean;
  reason?: string | null;
  serviceName?: string;
  registryDid?: string | null;
  /// The account the service's APPLICATION proved, as the platform records
  /// it. What the log and the registry say is `accounts`.
  account?: string | null;
  /// The account the log's head names and the one the registry binds, as the
  /// registrar bridge reads both (account-rebind-guard T2). Absent from an
  /// older platform; `registryKnown:false` when the registry view could not
  /// be read, so «binds none» is never said on a guess.
  accounts?: {log: string | null; registry: string | null; registryKnown?: boolean};
  /// Whose each publishing key is, by multikey (log-key-pairing D4): the
  /// platform's note, kept beside the log and never in it. It never decides
  /// who may sign; for a key the log does not list it means nothing.
  labels?: Record<string, LogLabel>;
  /// Set when the platform could not read its notes, so an empty `labels`
  /// is not mistaken for «nobody was noted».
  labelsError?: string;
}

export interface LogLabel {
  email: string;
  addedAt?: string | null;
  /// Set when the user was removed from the service dashboard: from then on
  /// the platform refuses what the key signs (D5).
  removedAt?: string | null;
}

export async function logPlan(projectId: string): Promise<LogPlan> {
  return httpGetJSON(`${customerPrefix()}/log/plan/${encodeURIComponent(projectId)}`, signUpToken(), MINE);
}

/// The APPLICANT's plan read (log-before-first-schema T2): before any project
/// exists, the DID template a log for this NAME would carry. The slug rule
/// lives on the bridge — the same one the key store, the file tree and
/// registration use — so asking is free and freezes nothing.
export async function logPlanForApplication(name: string): Promise<{
  ok: boolean; slug?: string; host?: string; didTemplate?: string; exists?: boolean; error?: string;
}> {
  return httpGetJSON(`${customerPrefix()}/log/plan-for-application?name=${encodeURIComponent(name)}`, signUpToken(), MINE);
}

/// The sitting's wallet prompt at APPLICATION time (T2, D5): the draft's
/// proven account witnesses the genesis this browser just signed. Not a gate
/// — Submit does not wait on it, and the head can still be witnessed from
/// the key panel after approval.
export async function logAttestPrepareForDraft(draftId: string, did: string, versionId: string): Promise<{ok: boolean; xdr?: string; error?: string; reason?: string}> {
  return httpPostJSON(`${customerPrefix()}/log/attest/prepare-for-draft`, {draftId, did, versionId}, signUpToken(), MINE);
}

export async function logAttestSubmitForDraft(draftId: string, signedXdr: string): Promise<{ok: boolean; txId?: string; error?: string; reason?: string}> {
  return httpPostJSON(`${customerPrefix()}/log/attest/submit-for-draft`, {draftId, signedXdr}, signUpToken(), MINE);
}

/// One ceremony: a genesis is one entry, a rotation is two (pre-rotation
/// admits only keys the previous entry committed). `label` is the owner's
/// note of whose publishing key the ceremony adds; the platform keeps it
/// beside the log, and only when the entry that lists the key has landed.
export async function publishLogEntries(projectId: string, entries: unknown[], label?: {multikey: string; email: string}) {
  return httpPostJSON(`${customerPrefix()}/log/entries`, {projectId, entries, ...(label ? {label} : {})}, signUpToken(), MINE);
}

/// The head attestation (webvh-log D4): the platform builds and simulates the
/// transaction, the owner's WALLET signs it, the platform relays the signed
/// bytes unchanged. Nothing here can sign, which is the point — the account
/// that owns the log is the account that witnesses it.
/// `ignoreErrors` on purpose: these refusals belong to the KEY PANEL, which
/// says what to do about each one, not to the app's global error sink — which
/// rendered a relayed `{message, reason}` object as «[object Object]» on prod
/// (2026-09-09) and swallowed the sentence that would have named the cause.
const MINE = {ignoreErrors: true};

export async function logAttestPrepare(projectId: string, versionId: string): Promise<{ok: boolean; xdr?: string; error?: string; reason?: string}> {
  return httpPostJSON(`${customerPrefix()}/log/attest/prepare`, {projectId, versionId}, signUpToken(), MINE);
}

export async function logAttestSubmit(projectId: string, signedXdr: string): Promise<{ok: boolean; txId?: string; error?: string; reason?: string}> {
  return httpPostJSON(`${customerPrefix()}/log/attest/submit`, {projectId, signedXdr}, signUpToken(), MINE);
}

export async function logAttestation(projectId: string): Promise<{ok: boolean; attestation: {version_id?: string; attested?: number; by?: string} | null}> {
  return httpGetJSON(`${customerPrefix()}/log/attestation/${encodeURIComponent(projectId)}`, signUpToken(), MINE);
}

/// One resource of the service's log as stored, by digest: in practice the
/// schema list, read before the key that signed it is removed
/// (log-key-pairing D6). A digest nothing is stored under is refused with
/// 404, which the key panel reads as «nothing published yet».
export async function logResource(projectId: string, digest: string): Promise<{ok: boolean; resource?: Record<string, unknown>}> {
  return httpGetJSON(`${customerPrefix()}/log/resource/${encodeURIComponent(projectId)}/${encodeURIComponent(digest)}`, signUpToken(), MINE);
}

/// Publish a resource the owner's key signed here: the schema list, signed
/// again when the key that signed it leaves. The bridge verifies it against
/// the DID document the log states before it stores anything.
export async function publishLogResource(projectId: string, resource: Record<string, unknown>): Promise<{ok: boolean; id?: string; digest?: string}> {
  return httpPostJSON(`${customerPrefix()}/log/resource`, {projectId, resource}, signUpToken(), MINE);
}
